import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import { type Session } from '@/types/session';
import { queryKeys } from '@/hooks/api/queryKeys';
import { axiosInstance as axios } from '@/utils/axiosConfig';

type TwoFaStep = 'none' | 'prompt' | 'setup';
type TwoFaErrors = 'validate_login_totp_required' | 'validate_login_totp_unconfigured';

export interface Credentials2fa {
  email: string;
  password: string;
}

export interface LoginErrorResponse {
  errors?: {
    message: string;
    errorCode: TwoFaErrors | 'generic_error';
    secret?: string;
    dataUri?: string;
    expires?: number; // Unix timestamp when QR code and secret expire
  }[];
}

export interface SignInMutationParams {
  email: string;
  password: string;
  remember?: boolean;
  totpCode?: string;
}

export async function signIn({ email, password, remember, totpCode }: SignInMutationParams) {
  const { data } = await axios.post<{ session: Session }>(
    '/v1/live-app/session',
    {
      email,
      password,
      remember,
      totpCode
    },
    {
      withCredentials: true
    }
  );

  return data.session;
}

/**
 * React hook for handling user sign-in with support for two-factor authentication (2FA) using TOTP.
 *
 * Manages 2FA state, credentials, and setup data, and provides helper functions to refresh 2FA setup information and clear credentials. Integrates with React Query to perform the sign-in mutation and handle 2FA-related error states, including prompting for TOTP codes or initiating 2FA setup when required.
 *
 * @returns An object containing the current 2FA step, stored credentials, 2FA setup data, helper functions for refreshing and clearing credentials, and the mutation object for sign-in.
 */
export function useSignInMutation() {
  const queryClient = useQueryClient();

  const [credentials2fa, setCredentials2fa] = useState<Credentials2fa>({
    email: '',
    password: ''
  });

  const [twoFaStep, setTwoFaStep] = useState<TwoFaStep>('none');

  const [twoFaSetupData, setTwoFaSetupData] = useState<{
    secret: string;
    dataUri: string;
    expires?: number; // Unix timestamp when QR code and secret expire
  } | null>(null);

  const clearCredentials2fa = () => {
    setCredentials2fa({
      email: '',
      password: ''
    });
  };

  const refreshTwoFaSetup = async (credentialsToUse?: Credentials2fa) => {
    const creds = credentialsToUse || credentials2fa;

    if (!creds.email || !creds.password) {
      return;
    }

    try {
      await signIn({
        email: creds.email,
        password: creds.password
      });
    } catch (error) {
      // This will trigger the onError handler which will update twoFaSetupData
      const axiosError = error as AxiosError;
      const response = axiosError.response?.data as LoginErrorResponse;
      const errorCode = response?.errors?.[0]?.errorCode as TwoFaErrors;

      if (errorCode === 'validate_login_totp_unconfigured') {
        setTwoFaSetupData({
          secret: response.errors?.[0]?.secret ?? '',
          dataUri: response.errors?.[0]?.dataUri ?? '',
          expires: response.errors?.[0]?.expires
        });
      }
    }
  };

  const clearCredentials = () => {
    clearCredentials2fa();
    setTwoFaStep('none');
    setTwoFaSetupData(null);
  };

  return {
    twoFaStep,
    credentials2fa,
    twoFaSetupData,
    refreshTwoFaSetup,
    clearCredentials,
    ...useMutation({
      mutationFn: ({ email, password, remember, totpCode }: SignInMutationParams) =>
        signIn({ email, password, remember, totpCode }),
      onSuccess: (response) => {
        queryClient.setQueryData([queryKeys.auth.session], response);
        void queryClient.invalidateQueries({ queryKey: [queryKeys.entryPages] });

        // Clear 2FA credentials and state after successful login
        clearCredentials();
      },
      onError: (error: AxiosError, variables) => {
        const response = error.response?.data as LoginErrorResponse;
        const errorCode = response?.errors?.[0]?.errorCode as TwoFaErrors;

        if (errorCode === 'validate_login_totp_required') {
          setTwoFaStep('prompt');
          setCredentials2fa({
            email: variables.email,
            password: variables.password
          });
          return;
        }

        if (errorCode === 'validate_login_totp_unconfigured') {
          setTwoFaStep('setup');
          setCredentials2fa({
            email: variables.email,
            password: variables.password
          });
          setTwoFaSetupData({
            secret: response.errors?.[0]?.secret ?? '',
            dataUri: response.errors?.[0]?.dataUri ?? '',
            expires: response.errors?.[0]?.expires
          });
          return;
        }

        // Only clear credentials for non-2FA errors
        setTwoFaStep('none');
        clearCredentials2fa();
        setTwoFaSetupData(null);
        throw error;
      }
    })
  };
}
